import React, { useCallback, useRef, useState } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetError,
  UseFormClearErrors,
} from "react-hook-form";
import { IFieldProperties } from "../InputFields.types";
import { checkDigaCode } from "../../../../util/Api/checkDigaCode";
import { sanitizeContent as s } from "../../../../util/functions";
import block from "bem-css-modules";
import styles from "./DiGACodeInput.module.scss";

const DIGA_CODE_LENGTH = 16;

type CheckState = "idle" | "checking" | "valid" | "invalid";

const DiGACodeInput = (props: {
  fieldData: IFieldProperties;
  register: UseFormRegister<any>;
  errors: FieldErrors;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
}) => {
  const {
    fieldData: {
      label,
      title,
      text,
      placeholder,
      validationText,
      settings: [{ crmProperty, required, validation, customValidationRule }],
    },
    register,
    errors,
    setError,
    clearErrors,
  } = props;

  const b = block(styles);
  const [checkState, setCheckState] = useState<CheckState>("idle");
  const lastCheckedCode = useRef<string>("");
  const checkErrorMessage = useRef<string>("");

  function isValid(value: string): boolean {
    try {
      const customRegex = new RegExp(customValidationRule);
      return customRegex.test(value);
    } catch {
      return false;
    }
  }

  const handleCustomValidation = (value: string) => {
    if (value === "") return true;
    if (checkState === "invalid") return checkErrorMessage.current || validationText;
    return validation && customValidationRule ? isValid(value) : true;
  };

  const handleBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();

      if (value.length !== DIGA_CODE_LENGTH) {
        setCheckState("idle");
        return;
      }

      if (value === lastCheckedCode.current) return;

      setCheckState("checking");
      clearErrors(label);
      lastCheckedCode.current = value;

      const result = await checkDigaCode(value);

      // Guard against stale responses from earlier requests
      if (value !== lastCheckedCode.current) return;

      if (result.valid) {
        setCheckState("valid");
        clearErrors(label);
      } else {
        setCheckState("invalid");
        checkErrorMessage.current = result.error;
        setError(label, { type: "manual", message: result.error });
      }
    },
    [label, clearErrors, setError],
  );

  const { onBlur: rhfOnBlur, ...registerRest } = register(label, {
    validate: handleCustomValidation,
    required,
  });

  const composedOnBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    rhfOnBlur(e);
    await handleBlur(e);
  };

  const stateModifier =
    checkState === "valid"
      ? "valid"
      : checkState === "checking"
        ? "checking"
        : undefined;

  return (
    <div className={`${b()} ${stateModifier ? b(stateModifier) : ""}`}>
      {title && (
        <h3
          className={b("title")}
          dangerouslySetInnerHTML={{ __html: s(title) }}
        />
      )}
      {text && (
        <div
          className={b("text")}
          dangerouslySetInnerHTML={{ __html: s(text) }}
        />
      )}
      <input
        {...registerRest}
        id={crmProperty}
        data-crm-field={crmProperty}
        placeholder={placeholder}
        type="text"
        maxLength={DIGA_CODE_LENGTH}
        onBlur={composedOnBlur}
      />
      {checkState === "checking" && (
        <div className={`${b("status")} ${b("status", { checking: true })}`}>
          ...
        </div>
      )}
      {checkState === "valid" && (
        <div className={`${b("status")} ${b("status", { valid: true })}`}>
          &#10003;
        </div>
      )}
      {errors[label] && (
        <div
          className={b("error")}
          dangerouslySetInnerHTML={{
            __html: s((errors[label]?.message as string) || validationText),
          }}
        />
      )}
    </div>
  );
};

export default DiGACodeInput;
